import { hexDist, hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import type { FireState } from '../types.ts';

export type { FireState };

/**
 * The Slope of the kilns (docs/sendas/druida.md, chapter 2, summer): the grounds, the same from the seed in the sim and
 * the drawing, integers only. Up is −y (the top of the screen: up the slope, toward the old pine).
 *
 * - `kilnLayout` (act 1, the kilns): Hob's slope, tall yellow grass and hot stone; six charcoal kilns (earth mounds
 *   smoking) spread over it, each a cell nobody walks, Hob's spot at its foot; outcrops here and there.
 * - `pineLayout` (act 2, Olmo's pine): the old pine at the top on its rock, the strip Hob opens across the slope below
 *   it (a row of trees to fell, Hob's spot at each), the great fire coming up from the valley at the foot.
 * - `counterLayout` (act 3, the test, «El contrafuego»): the slope in patches of grass (each a few cells), banded from
 *   the top to the foot; the old pine on its band of bare stone across the middle; the great fire at the foot.
 *
 * And the counterfire's rule (§4, puzzle 2), pure, for the sim and its solver: a patch lit burns `burn` and is black
 * after; fire never goes through black. A patch that has burned `catch` lights the patches next to it the way the wind
 * blows (up the slope or down it), every tick while it still burns: a patch burning when the wind turns sends its fire
 * the new way. The wind turns once (`flip`), from up to down. The pine stands between the patches just above it and the
 * ones just below: a fire that the wind carries into it scorches it (a mark, once a patch). The great fire comes up
 * from the foot a band each `climb`, whatever the wind: it lights the grass just above what it lit last; it never
 * passes what already burns or is black (where it meets the counterfire, it dies); into the pine, it burns it. The pine
 * wants heat without fire: a black patch just above it and just below, all of those below burnt or burning (the great
 * fire can't come in), none of the ones next to it still burning, and the great fire met or dead.
 */

export const KILN_RADIUS = 8;
export const PINE_RADIUS = 9;
export const COUNTER_RADIUS = 9;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (h: Hex): string => `${h.q},${h.r}`;

/* ---------- the kilns ---------- */

export interface KilnLayout {
  radius: number;
  rocks: Hex[];
  /** The kilns' cells, in Hob's order (from the one nearest him, each next the nearest left). */
  kilns: Hex[];
  /** Where Hob stands to cover each (at its foot, sim points). */
  spots: { x: number; y: number }[];
  /** For the drawing: the stone outcrops (rocks too), the grass. */
  outcrops: Hex[];
  grass: Hex[];
  hob: { x: number; y: number };
  start: { x: number; y: number };
  /** Where the crows come in from (the smoke at the rim, sim points). */
  rim: { x: number; y: number }[];
}

export const kilnLayout = (seed: number, n: number): KilnLayout => {
  const R = KILN_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x6b11));
  // The kilns: near the slope's middle (four hexes out, five if they don't fit), three hexes apart at least, none at
  // the middle where Hob starts.
  let kilns: Hex[] = [];
  for (const far of [4, 5]) {
    const cand = allCells(far).filter((h) => hexLen(h.q, h.r) >= 2 && h.r <= far - 1);
    kilns = [];
    for (let guard = 0; kilns.length < n && guard < 600; guard++) {
      const h = cand[rngInt(rng, cand.length)] as Hex;
      if (kilns.some((o) => hexDist(o, h) < 3)) continue;
      kilns.push(h);
    }
    if (kilns.length === n) break;
  }
  // Hob's order: from the middle, each next the nearest left.
  const order: Hex[] = [];
  let at: Hex = { q: 0, r: 0 };
  const left = [...kilns];
  while (left.length) {
    let bi = 0;
    for (let i = 1; i < left.length; i++) if (hexDist(left[i] as Hex, at) < hexDist(left[bi] as Hex, at)) bi = i;
    at = left.splice(bi, 1)[0] as Hex;
    order.push(at);
  }
  const kilnAt = new Set(order.map(key));
  // Outcrops: a few cells of bare hot stone, away from the kilns and their feet.
  const outcrops: Hex[] = [];
  const near = (h: Hex, d: number): boolean => order.some((k) => hexDist(k, h) <= d);
  for (let guard = 0; outcrops.length < 7 && guard < 400; guard++) {
    const h = { q: rngRange(rng, -7, 7), r: rngRange(rng, -7, 7) };
    // (None at the foot of the slope, where the opening's people stand.)
    if (hexLen(h.q, h.r) > R - 1 || hexLen(h.q, h.r) < 2 || h.r >= 4 || near(h, 1) || outcrops.some((o) => key(o) === key(h))) continue;
    outcrops.push(h);
  }
  const rocks = [...order, ...outcrops].sort(sortHex);
  const rockAt = new Set(rocks.map(key));
  const grass = allCells(R).filter((h) => !rockAt.has(key(h)) && !kilnAt.has(key(h)));
  const spots = order.map((h) => {
    const p = hexToWorld(h.q, h.r);
    return { x: p.x, y: p.y + 760 };
  });
  const rim: { x: number; y: number }[] = [];
  for (let k = 0; k < 6; k++) {
    const q = [7, 0, -7, -7, 0, 7][k] as number;
    const r = [0, 7, 7, 0, -7, -7][k] as number;
    rim.push(hexToWorld(idiv(q * 6, 7), idiv(r * 6, 7)));
  }
  return { radius: R, rocks, kilns: order, spots, outcrops: outcrops.sort(sortHex), grass, hob: hexToWorld(0, 0), start: hexToWorld(0, 2), rim };
};

/* ---------- Olmo's pine: the strip ---------- */

export interface PineLayout {
  radius: number;
  rocks: Hex[];
  /** The pine's rock at the top (its cells), the pine's own cell. */
  pine: Hex[];
  trunk: Hex;
  /** The strip's row (r) and the trees on it Hob fells, west to east; where he stands at each (sim points). */
  strip: number;
  trees: Hex[];
  spots: { x: number; y: number }[];
  /** For the drawing: the grass between, the stone. */
  grass: Hex[];
  /** The great fire's line at the start (y: the foot) and the strip's (y). */
  foot: number;
  line: number;
  hob: { x: number; y: number };
  start: { x: number; y: number };
  /** Where the crows and the wolves come in from (sim points). */
  rim: { x: number; y: number }[];
}

export const pineLayout = (seed: number, trees: number): PineLayout => {
  const R = PINE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x914e));
  const trunk = { q: 3, r: -7 };
  const pine: Hex[] = [trunk, { q: 2, r: -7 }, { q: 3, r: -8 }, { q: 4, r: -8 }, { q: 4, r: -7 }];
  // The strip: across the slope three rows under the pine, its trees two hexes apart, the ends jittered.
  const strip = -3;
  const lo = -4 - rngInt(rng, 2);
  const out: Hex[] = [];
  for (let k = 0; out.length < trees; k++) {
    const x = (lo + k * 2) * 1000 + (rngInt(rng, 2) === 0 ? 0 : 500);
    const q = idiv(x - strip * 500, 1000);
    const h = { q, r: strip };
    if (hexLen(h.q, h.r) > R - 1) break;
    out.push(h);
  }
  const pineAt = new Set(pine.map(key));
  const rocks = [...pine, ...out].sort(sortHex);
  const rockAt = new Set(rocks.map(key));
  const grass = allCells(R).filter((h) => !rockAt.has(key(h)) && !pineAt.has(key(h)));
  const spots = out.map((h) => {
    const p = hexToWorld(h.q, h.r);
    return { x: p.x, y: p.y + 760 };
  });
  const rim: { x: number; y: number }[] = [hexToWorld(-8, 4), hexToWorld(8, -4), hexToWorld(-6, 7), hexToWorld(6, 2)];
  const first = spots[0] as { x: number; y: number };
  return { radius: R, rocks, pine, trunk, strip, trees: out, spots, grass, foot: (R - 1) * ROW + 400, line: strip * ROW + 500, hob: { x: first.x - 600, y: first.y + 400 }, start: { x: first.x + 600, y: first.y + 1600 }, rim };
};

/* ---------- the counterfire ---------- */

/**
 * The slope's patches: each its cells and its band (0 the top), the patches touching it in the band above (`up`) and
 * below (`down`); the pine's neighbours above (`pineUp`: the pine is in their way down) and below (`pineDown`); the
 * foot's patches, where the great fire comes in.
 */
export interface FireNet {
  cells: Hex[][];
  band: number[];
  up: number[][];
  down: number[][];
  pineUp: number[];
  pineDown: number[];
  foot: number[];
}

/** The rule's numbers (content DRUID_PATH.counter) and the wind's turn and the great fire's start (the layout's). */
export interface FireParams {
  burn: number;
  catch: number;
  climb: number;
  flip: number;
  start: number;
}

export type FireEvent = { k: 'lit' | 'black' | 'mark' | 'meet' | 'climb' | 'pine'; i: number; grand?: boolean };

/** The band of bare stone the pine stands on, and how many bands of grass under it. */
export const PINE_BAND = 2;
const BANDS = 8;
/** The pine's span across the slope (sim x, each side of the middle). */
export const PINE_SPAN = 2600;

export const fireFresh = (net: FireNet): FireState => ({
  st: net.cells.map(() => 0),
  lit: net.cells.map(() => -1),
  grand: net.cells.map(() => 0),
  entered: net.cells.map(() => 0),
  front: [],
  next: -1,
  marks: 0,
  burnt: 0,
  meets: 0,
});

export const fireClone = (st: FireState): FireState => ({ st: [...st.st], lit: [...st.lit], grand: [...st.grand], entered: [...st.entered], front: [...st.front], next: st.next, marks: st.marks, burnt: st.burnt, meets: st.meets });

/** Sets a patch of grass burning (a tap's ember, the wyvern's, the great fire's). False if it isn't grass. */
export const fireLight = (st: FireState, i: number, t: number, grand = false, out?: FireEvent[]): boolean => {
  if (st.st[i] !== 0) return false;
  st.st[i] = 1;
  st.lit[i] = t;
  st.grand[i] = grand ? 1 : 0;
  out?.push({ k: 'lit', i, grand });
  return true;
};

/** Whether the wind blows down the slope at a tick (it turns once, at `flip`). */
export const windDown = (P: FireParams, t: number): boolean => t >= P.flip;

/** The rule's tick t: burnt out, carried by the wind (into the pine: a mark), the great fire up a band. */
export const fireStep = (net: FireNet, P: FireParams, st: FireState, t: number, out?: FireEvent[]): void => {
  const n = st.st.length;
  for (let i = 0; i < n; i++) {
    if (st.st[i] === 1 && t - (st.lit[i] as number) >= P.burn) {
      st.st[i] = 2;
      out?.push({ k: 'black', i });
    }
  }
  const down = windDown(P, t);
  for (let i = 0; i < n; i++) {
    if (st.st[i] !== 1 || st.grand[i] || t - (st.lit[i] as number) < P.catch) continue;
    for (const j of (down ? net.down[i] : net.up[i]) as number[]) fireLight(st, j, t, false, out);
    // Into the pine: once a patch.
    const toPine = down ? net.pineUp.includes(i) : net.pineDown.includes(i);
    if (toPine && !st.entered[i]) {
      st.entered[i] = 1;
      st.marks++;
      out?.push({ k: 'mark', i });
    }
  }
  // The great fire: at the foot at its start, then a band up each climb.
  if (st.next < 0 && t >= P.start) {
    st.next = t + P.climb;
    for (const i of net.foot) if (fireLight(st, i, t, true, out)) st.front.push(i);
    out?.push({ k: 'climb', i: BANDS - 1 });
  } else if (st.next >= 0 && t >= st.next && st.front.length) {
    st.next = t + P.climb;
    const next: number[] = [];
    let band = -1;
    for (const i of st.front) {
      if (net.pineDown.includes(i) && !st.burnt) {
        st.burnt = 1;
        out?.push({ k: 'pine', i });
      }
      let met = false;
      for (const j of net.up[i] as number[]) {
        if (st.st[j] === 0) {
          fireLight(st, j, t, true, out);
          if (!next.includes(j)) next.push(j);
          band = net.band[j] as number;
        } else if (!st.grand[j]) met = true;
      }
      if (met) {
        st.meets++;
        out?.push({ k: 'meet', i });
      }
    }
    next.sort((a, b) => a - b);
    st.front = next;
    if (band >= 0) out?.push({ k: 'climb', i: band });
  }
};

/** The pine has its heat: black just above and below, nothing below it the great fire can take, none next to it burning, the great fire met or dead. */
export const fireWon = (net: FireNet, st: FireState): boolean => {
  if (st.burnt || !net.pineUp.some((i) => st.st[i] === 2) || !net.pineDown.some((i) => st.st[i] === 2)) return false;
  if (net.pineDown.some((i) => st.st[i] === 0)) return false;
  if ([...net.pineUp, ...net.pineDown].some((i) => st.st[i] === 1)) return false;
  return st.meets > 0 || (st.next >= 0 && st.front.length === 0);
};

/** A plan played on the rule alone: the patches lit at their ticks, up to a horizon. */
export const firePlay = (net: FireNet, P: FireParams, lights: { i: number; t: number }[], horizon: number): { won: boolean; marks: number; burnt: boolean; t: number } => {
  const st = fireFresh(net);
  const by = [...lights].sort((a, b) => a.t - b.t || a.i - b.i);
  let k = 0;
  for (let t = 0; t <= horizon; t++) {
    while (k < by.length && (by[k] as { t: number }).t <= t) fireLight(st, (by[k++] as { i: number }).i, t);
    fireStep(net, P, st, t);
    if (st.burnt) return { won: false, marks: st.marks, burnt: true, t };
    if (fireWon(net, st)) return { won: true, marks: st.marks, burnt: false, t };
  }
  return { won: false, marks: st.marks, burnt: false, t: horizon };
};

export interface CounterLayout {
  radius: number;
  rocks: Hex[];
  net: FireNet;
  /** The patches' middles (sim points), the pine's cell and its stone band's cells, the scrub past the patches (rocks). */
  mid: { x: number; y: number }[];
  pine: Hex;
  stone: Hex[];
  scrub: Hex[];
  /** The wind's turn and the great fire's start (ticks, from the fight's start). */
  flip: number;
  start: number;
  hero: { x: number; y: number };
  /** Where the crows come in from (sim points). */
  rim: { x: number; y: number }[];
}

const BAND_ROW0 = -8;
const bandOf = (r: number): number => imin(BANDS - 1, idiv(r - BAND_ROW0, 2));

/** The patches, the pine and the bands from a seed's cuts across the slope (each band its own cuts: they never line up). */
const counterNet = (seed: number, R: number): { net: FireNet; rocks: Hex[]; stone: Hex[]; scrub: Hex[]; mid: { x: number; y: number }[] } => {
  const rng = seedRng(seed);
  // Each band of grass: four patches, cut at three points across, jittered.
  const cuts: number[][] = [];
  for (let b = 0; b < BANDS; b++) {
    const off = rngRange(rng, -3, 3) * 250;
    cuts.push([-2750 + off + rngRange(rng, -2, 2) * 250, off + rngRange(rng, -1, 1) * 250, 2750 + off + rngRange(rng, -2, 2) * 250]);
  }
  const cells: Hex[][] = [];
  const band: number[] = [];
  const span: { lo: number; hi: number }[] = [];
  const idx = new Map<string, number>();
  const stone: Hex[] = [];
  const scrub: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (h.r < BAND_ROW0 || Math.abs(p.x) > 6200) {
      scrub.push(h);
      rocks.push(h);
      continue;
    }
    const b = bandOf(h.r);
    if (b === PINE_BAND) {
      stone.push(h);
      continue;
    }
    const c = cuts[b] as number[];
    let col = 0;
    while (col < 3 && p.x >= (c[col] as number)) col++;
    const k = `${b}:${col}`;
    let i = idx.get(k);
    if (i === undefined) {
      i = cells.length;
      idx.set(k, i);
      cells.push([]);
      band.push(b);
      span.push({ lo: col === 0 ? -9000 : (c[col - 1] as number), hi: col === 3 ? 9000 : (c[col] as number) });
    }
    (cells[i] as Hex[]).push(h);
  }
  // Renumbered from the top, west to east: a patch's index reads as its place.
  const order = cells.map((_, i) => i).sort((a, b) => (band[a] as number) - (band[b] as number) || (span[a] as { lo: number }).lo - (span[b] as { lo: number }).lo);
  const C = order.map((i) => (cells[i] as Hex[]).sort(sortHex));
  const B = order.map((i) => band[i] as number);
  const S = order.map((i) => span[i] as { lo: number; hi: number });
  const overlap = (a: { lo: number; hi: number }, b: { lo: number; hi: number }): boolean => imin(a.hi, b.hi) - imax(a.lo, b.lo) >= 400;
  const pine = { lo: -PINE_SPAN, hi: PINE_SPAN };
  const up: number[][] = C.map(() => []);
  const down: number[][] = C.map(() => []);
  const pineUp: number[] = [];
  const pineDown: number[] = [];
  for (let i = 0; i < C.length; i++)
    for (let j = 0; j < C.length; j++) {
      if ((B[j] as number) === (B[i] as number) - 1 && overlap(S[i] as { lo: number; hi: number }, S[j] as { lo: number; hi: number })) {
        (up[i] as number[]).push(j);
        (down[j] as number[]).push(i);
      }
    }
  for (let i = 0; i < C.length; i++) {
    if ((B[i] as number) === PINE_BAND - 1 && overlap(S[i] as { lo: number; hi: number }, pine)) pineUp.push(i);
    if ((B[i] as number) === PINE_BAND + 1 && overlap(S[i] as { lo: number; hi: number }, pine)) pineDown.push(i);
  }
  const foot = C.map((_, i) => i).filter((i) => B[i] === BANDS - 1);
  const mid = C.map((cs) => {
    let x = 0;
    let y = 0;
    for (const h of cs) {
      const p = hexToWorld(h.q, h.r);
      x += p.x;
      y += p.y;
    }
    return { x: idiv(x, cs.length), y: idiv(y, cs.length) };
  });
  return { net: { cells: C, band: B, up, down, pineUp, pineDown, foot }, rocks: rocks.sort(sortHex), stone: stone.sort(sortHex), scrub: scrub.sort(sortHex), mid };
};

/**
 * The slope for a fight: the first of a seed's tries whose pine has two or three patches above and three below, where
 * the plan that knows wins clean, cutting the fire at once burns it and lighting it all after the turn scorches it
 * twice (a test holds it over many seeds).
 * The wind turns between `flipMin` and `flipMax`; the great fire reaches the patches under the pine `room` after.
 */
export const counterLayout = (seed: number, P: { burn: number; catch: number; climb: number; flipMin: number; flipMax: number; room: number }): CounterLayout => {
  const R = COUNTER_RADIUS;
  let got: ReturnType<typeof counterNet> | null = null;
  let flip = 0;
  let start = 0;
  for (let k = 0; k < 40; k++) {
    const s = deriveSeed(seed, 0xc0f1 + k);
    const n = counterNet(s, R);
    const rng = seedRng(deriveSeed(s, 7));
    const f = rngRange(rng, idiv(P.flipMin, 12), idiv(P.flipMax, 12)) * 12;
    // The great fire reaches the band under the pine (four climbs from the foot) at the turn and its room.
    const st = f + P.room - (BANDS - 1 - (PINE_BAND + 1)) * P.climb;
    got = n;
    flip = f;
    start = imax(0, st);
    if (counterOk(n.net, { burn: P.burn, catch: P.catch, climb: P.climb, flip: f, start })) break;
  }
  const L = got as ReturnType<typeof counterNet>;
  const pine = { q: 0, r: -3 };
  // (The pine's own cell: nobody walks through its trunk.)
  const rocks = [...L.rocks, pine].sort(sortHex);
  const mid = L.mid;
  const rim: { x: number; y: number }[] = [hexToWorld(-8, 0), hexToWorld(8, -8), hexToWorld(-4, -5), hexToWorld(5, 3)];
  return { radius: R, rocks, net: L.net, mid, pine, stone: L.stone, scrub: L.scrub, flip, start, hero: hexToWorld(1, -3), rim };
};

/** The plans the rule is checked with: who knows (above early, below after the turn), and the obvious ones. */
export const counterPlans = (net: FireNet, P: FireParams): Record<'knows' | 'cut' | 'after', { i: number; t: number }[]> => ({
  knows: [...net.pineUp.map((i) => ({ i, t: 60 })), ...net.pineDown.map((i, k) => ({ i, t: P.flip + 24 + k * 24 }))],
  // Cutting the fire at once: the patches under the pine and the band under them, lit right away.
  cut: [...net.pineDown, ...net.cells.map((_, i) => i).filter((i) => net.band[i] === PINE_BAND + 2)].map((i, k) => ({ i, t: 36 + k * 12 })),
  // Waiting for the turn to light it all: above and below, after.
  after: [...net.pineDown, ...net.pineUp].map((i, k) => ({ i, t: P.flip + 24 + k * 24 })),
});

/** A slope worth playing: two or three patches above the pine, three below; who knows wins clean; cutting the fire at once burns it (three marks), lighting it all after the turn scorches it twice. */
export const counterOk = (net: FireNet, P: FireParams): boolean => {
  if (net.pineUp.length < 2 || net.pineUp.length > 3 || net.pineDown.length !== 3) return false;
  const H = P.flip + 12 * 120;
  const plans = counterPlans(net, P);
  const k = firePlay(net, P, plans.knows, H);
  if (!k.won || k.marks) return false;
  const c = firePlay(net, P, plans.cut, H);
  const a = firePlay(net, P, plans.after, H);
  return c.marks >= 3 && a.marks >= 2;
};
